import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { workspaceApi, pipelineApi } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { useToast } from '@/stores/toast-store'
import { ColorDot } from '@/components/ui/color-dot'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/ui/empty-state'
import type { PipelineStatusResponse } from '@/types/pipeline'

export default function WorkspacePipelinePage() {
  const { slug } = useParams<{ slug: string }>()
  const queryClient = useQueryClient()
  const currentUserId = useAuthStore((s) => s.user?.id)
  const { toast } = useToast()

  const { data: members } = useQuery({
    queryKey: ['workspace', slug, 'members'],
    queryFn: () => workspaceApi.getMembers(slug!),
    enabled: !!slug,
  })

  const { data: workspace } = useQuery({
    queryKey: ['workspace', slug],
    queryFn: () => workspaceApi.getWorkspace(slug!),
    enabled: !!slug,
  })

  const {
    data: statuses,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['workspace', slug, 'pipeline'],
    queryFn: () => pipelineApi.getStatuses(slug!),
    enabled: !!slug,
  })

  // Derive role from members list
  const myMember = members?.find((m) => m.user_id === currentUserId)
  const myRole = myMember?.role
  const canEdit = myRole === 'owner' || myRole === 'admin'
  const canDelete = myRole === 'owner'

  // ── Add form state ───────────────────────────────────────────────────────────
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState('#6b7280')

  // ── Edit state ───────────────────────────────────────────────────────────────
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editColor, setEditColor] = useState('')

  // ── Delete confirm state ─────────────────────────────────────────────────────
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['workspace', slug, 'pipeline'] })

  const createMutation = useMutation({
    mutationFn: (data: { name: string; color: string }) =>
      pipelineApi.createStatus(slug!, data),
    onSuccess: () => {
      invalidate()
      setNewName('')
      setNewColor('#6b7280')
      toast({ title: 'Status berhasil ditambahkan' })
    },
    onError: (err) => {
      toast({ title: 'Gagal menambah status', description: (err as Error).message, variant: 'destructive' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; payload: Parameters<typeof pipelineApi.updateStatus>[2] }) =>
      pipelineApi.updateStatus(slug!, data.id, data.payload),
    onSuccess: () => {
      invalidate()
      setEditingId(null)
    },
    onError: (err) => {
      toast({ title: 'Gagal memperbarui status', description: (err as Error).message, variant: 'destructive' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => pipelineApi.deleteStatus(slug!, id),
    onSuccess: () => {
      invalidate()
      setDeletingId(null)
      toast({ title: 'Status berhasil dihapus' })
    },
    onError: (err) => {
      toast({ title: 'Gagal menghapus status', description: (err as Error).message, variant: 'destructive' })
    },
  })

  function startEdit(status: PipelineStatusResponse) {
    setEditingId(status.id)
    setEditName(status.name)
    setEditColor(status.color)
  }

  function cancelEdit() {
    setEditingId(null)
  }

  function handleReorder(status: PipelineStatusResponse, direction: 'up' | 'down') {
    if (!statuses) return
    const idx = statuses.findIndex((s) => s.id === status.id)
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    if (swapIdx < 0 || swapIdx >= statuses.length) return
    const neighbor = statuses[swapIdx]
    // Swap positions
    updateMutation.mutate({ id: status.id, payload: { position: neighbor.position } })
    updateMutation.mutate({ id: neighbor.id, payload: { position: status.position } })
  }

  function handleSetDefault(status: PipelineStatusResponse) {
    updateMutation.mutate({ id: status.id, payload: { is_default: true } })
  }

  function handleAdd() {
    if (!newName.trim()) return
    createMutation.mutate({ name: newName, color: newColor })
  }

  function handleSaveEdit(id: string) {
    updateMutation.mutate({ id, payload: { name: editName, color: editColor } })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Pipeline Status</h1>
        <p className="text-sm text-muted-foreground">
          {workspace?.name ?? slug} — kelola urutan dan nama status task.
        </p>
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-sm text-destructive">Gagal memuat pipeline. Coba refresh halaman.</p>
      )}

      {/* Status list */}
      {!isLoading && !error && statuses && (
        <div className="rounded-xl border border-border bg-card">
          {statuses.length === 0 ? (
            <div className="p-8">
              <EmptyState
                title="Belum ada status pipeline."
                description="Tambahkan status pertama menggunakan form di bawah."
              />
            </div>
          ) : (
            <ul className="divide-y">
              {statuses.map((status, idx) => (
                <li key={status.id} className="flex items-center gap-3 px-4 py-3">
                  {/* Color dot */}
                  <ColorDot color={status.color} />

                  {/* Name / inline edit */}
                  {editingId === status.id ? (
                    <div className="flex flex-1 items-center gap-2">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="flex-1 rounded border bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEdit(status.id)
                          if (e.key === 'Escape') cancelEdit()
                        }}
                        autoFocus
                      />
                      <input
                        type="color"
                        value={editColor}
                        onChange={(e) => setEditColor(e.target.value)}
                        className="h-7 w-8 cursor-pointer rounded border"
                        title="Pilih warna"
                      />
                      <button
                        onClick={() => handleSaveEdit(status.id)}
                        disabled={updateMutation.isPending}
                        className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
                      >
                        Simpan
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground"
                      >
                        Batal
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-1 items-center gap-2">
                      <span className="text-sm font-medium">{status.name}</span>
                      {status.is_default && (
                        <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-xs text-blue-600 dark:text-blue-400">
                          Default
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">pos:{status.position}</span>
                    </div>
                  )}

                  {/* Actions — only visible to owner/admin */}
                  {canEdit && editingId !== status.id && (
                    <div className="flex shrink-0 items-center gap-1">
                      {/* Reorder */}
                      <button
                        onClick={() => handleReorder(status, 'up')}
                        disabled={idx === 0 || updateMutation.isPending}
                        title="Naik"
                        className="rounded px-1 py-0.5 text-xs text-muted-foreground hover:bg-secondary disabled:opacity-30"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => handleReorder(status, 'down')}
                        disabled={idx === statuses.length - 1 || updateMutation.isPending}
                        title="Turun"
                        className="rounded px-1 py-0.5 text-xs text-muted-foreground hover:bg-secondary disabled:opacity-30"
                      >
                        ↓
                      </button>

                      {/* Set default */}
                      {!status.is_default && (
                        <button
                          onClick={() => handleSetDefault(status)}
                          disabled={updateMutation.isPending}
                          className="rounded border border-border px-2 py-0.5 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
                        >
                          Jadikan Default
                        </button>
                      )}

                      {/* Edit */}
                      <button
                        onClick={() => startEdit(status)}
                        title="Edit"
                        className="rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-secondary"
                      >
                        ✏️
                      </button>

                      {/* Delete — owner only */}
                      {canDelete && (
                        <button
                          onClick={() => setDeletingId(status.id)}
                          title="Hapus"
                          className="rounded px-1.5 py-0.5 text-xs text-destructive hover:bg-destructive/10"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Add form — only visible to owner/admin */}
      {canEdit && (
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Nama status baru..."
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <input
            type="color"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            className="h-9 w-10 cursor-pointer rounded-md border border-input"
            title="Pilih warna"
          />
          <button
            onClick={handleAdd}
            disabled={!newName.trim() || createMutation.isPending}
            className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
          >
            {createMutation.isPending ? 'Menyimpan...' : '+ Tambah Status'}
          </button>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deletingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6">
            <h2 className="text-base font-semibold tracking-tight">Hapus Status?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Semua task dengan status ini akan kehilangan statusnya (menjadi tidak ada status).
              Tindakan ini tidak bisa dibatalkan.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setDeletingId(null)}
                className="rounded border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                Batal
              </button>
              <button
                onClick={() => deleteMutation.mutate(deletingId)}
                disabled={deleteMutation.isPending}
                className="rounded bg-destructive px-3 py-1.5 text-sm text-white hover:opacity-90 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Menghapus...' : 'Hapus'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
